/**
 * @file receipt-item-columns.tsx
 * @description Колонки таблицы позиций приёмки — реестром (phase19.31, Б15).
 *
 * «Партия» и «Годен до» — отдельные колонки (раньше «Партия · до» одной, и
 * срок пропадал у строки черновика без номера партии). Тексты и тона — из
 * `receipt-item-format.ts`, здесь только вёрстка ячеек.
 *
 * Приёмка qa4 п. 4: «Партия» была уже 50px — «присвоит система» ломалась
 * по слогам, а номер партии вставал столбиком по букве. Теперь партия —
 * одной строкой (номер коротким видом «RCP-…019-1», полный — в подсказке),
 * а где места мало (черновик с панелью, 1024–1279), «Годен до» уходит второй
 * линией под партию (`foldExpiry`) и отдаёт свои 96px колонке «Партия».
 *
 * Приёмка QA-5: шапка свёрнутой колонки — просто «Партия» (вторая линия
 * «до 21.09.2027» говорит сама за себя, а «Партия · годен до» ломалась в две
 * строки, D10); «Указать ячейку» — значок и «Указать» одной строкой (D10);
 * на 1024–1279 у проведённой приёмки «Этикетки» без своей колонки
 * (`foldLabels`) — пометки уходят второй линией под название (D9: «⋯»
 * налезал на «Срок: не указан»).
 */

import { MapPin } from "lucide-react";
import type { ReactNode } from "react";

import { receiptExpiryView, receiptLotView } from "./receipt-item-format";
import { shortDocNumber } from "./short-number";
import type { DocumentTableColumn } from "../ui/DocumentItemsTable";
import type { GoodsReceiptItemDto } from "../../lib/api-wms";
import { cn } from "@/lib/utils";

/** Тон текста срока — реестром. */
const EXPIRY_TONE = {
  default: "text-on-surface",
  warning: "text-warning",
  muted: "text-on-surface-variant",
} as const;

interface Input {
  /** Черновик: партия без номера — «присвоит система». */
  draft: boolean;
  /** Срок у строки обязателен, но пуст (сервер: `missingExpiryItems`). */
  isMissing: (item: GoodsReceiptItemDto) => boolean;
  /** Колонка «Этикетки» — только у проведённой приёмки. */
  labelsCell?: (item: GoodsReceiptItemDto) => ReactNode;
  /** Черновик: «Указать ячейку» / адрес-кнопка для замены (Б16). */
  onSetBin?: (item: GoodsReceiptItemDto) => void;
  /** Мало места: «Годен до» — второй линией под партией, без своей колонки. */
  foldExpiry?: boolean;
  /** Мало места: «Этикетки» без своей колонки (пометки рисует строка под названием). */
  foldLabels?: boolean;
}

/** Ячейка строки: у черновика — кнопка (указать/сменить), иначе — текст. */
function binCell(item: GoodsReceiptItemDto, onSetBin?: (item: GoodsReceiptItemDto) => void): ReactNode {
  const code = item.bin?.code ?? null;
  if (!onSetBin) return <span className="font-mono text-xs text-on-surface-variant">{code ?? "—"}</span>;
  return (
    <button
      type="button"
      onClick={() => onSetBin(item)}
      aria-label={code ? `Сменить ячейку ${code}` : "Указать ячейку"}
      className={cn(
        "-mx-1 inline-flex min-h-8 items-center gap-1 whitespace-nowrap rounded-md px-1 text-left text-xs text-primary underline-offset-4 hover:underline",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        code && "font-mono",
      )}
    >
      {code ?? (
        <>
          <MapPin aria-hidden className="size-3.5 shrink-0" />
          Указать
        </>
      )}
    </button>
  );
}

/** Партия одной строкой: номер коротким видом, подпись «присвоит система» — без переноса. */
function lotCell(item: GoodsReceiptItemDto, draft: boolean): ReactNode {
  const lot = receiptLotView(item, draft);
  return (
    <span
      className={cn("block truncate whitespace-nowrap text-xs", lot.muted ? "text-on-surface-variant" : "font-mono")}
      title={lot.text}
    >
      {lot.muted ? lot.text : shortDocNumber(lot.text)}
    </span>
  );
}

/** Подпись перед сроком во второй линии под партией — по тону срока. */
const FOLDED_PREFIX = { default: "до ", warning: "срок ", muted: "" } as const;

/**
 * Срок годности: дата, «не указан» янтарным или «—». `folded` — вторая
 * линия под партией: «до 21.09.2027» / «срок не указан»; срок не ведётся —
 * линии нет.
 */
function expiryCell(item: GoodsReceiptItemDto, missing: boolean, folded = false): ReactNode {
  const view = receiptExpiryView(item, missing);
  if (folded && view.tone === "muted") return null;
  return (
    <span className={cn("block whitespace-nowrap text-xs tabular-nums", EXPIRY_TONE[view.tone])}>
      {folded ? FOLDED_PREFIX[view.tone] : null}
      {view.text}
    </span>
  );
}

export function receiptItemColumns({
  draft,
  isMissing,
  labelsCell,
  onSetBin,
  foldExpiry = false,
  foldLabels = false,
}: Input): DocumentTableColumn<GoodsReceiptItemDto>[] {
  const lotColumn: DocumentTableColumn<GoodsReceiptItemDto> = foldExpiry
    ? {
        key: "lot",
        label: "Партия",
        render: (item) => (
          <span className="flex min-w-0 flex-col gap-0.5">
            {lotCell(item, draft)}
            {expiryCell(item, isMissing(item), true)}
          </span>
        ),
      }
    : { key: "lot", label: "Партия", render: (item) => lotCell(item, draft) };
  const columns: DocumentTableColumn<GoodsReceiptItemDto>[] = [
    lotColumn,
    ...(foldExpiry
      ? []
      : [{ key: "expiry", label: "Годен до", width: "96px", render: (item: GoodsReceiptItemDto) => expiryCell(item, isMissing(item)) }]),
    {
      key: "bin",
      label: "Ячейка",
      width: "88px",
      render: (item) => binCell(item, onSetBin),
    },
    {
      key: "qty",
      label: "Кол-во",
      width: "72px",
      align: "right",
      render: (item) => <span className="tabular-nums">{item.quantity}</span>,
    },
  ];
  if (labelsCell && !foldLabels) columns.push({ key: "labels", label: "Этикетки", width: "104px", render: labelsCell });
  return columns;
}
